import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styles from "../../styles/pages/games/NeonDash.module.scss";

export default function NeonDash({ onExit }) {
  // --- Refs for DOM ---
  const canvasRef = useRef(null);

  const scoreRef = useRef(null);
  const bestRef = useRef(null);
  const multRef = useRef(null);
  const shieldRef = useRef(null);
  const strikesRef = useRef(null);
  const statusCardRef = useRef(null);
  const toastRef = useRef(null);

  const ovScoreRef = useRef(null);
  const ovBestRef = useRef(null);
  const ovMultRef = useRef(null);
  const ovHeartsRef = useRef(null);
  const ovFooterLeftRef = useRef(null);

  // --- React UI state (overlay visibility + mode + tips, sound button label) ---
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [overlayMode, setOverlayMode] = useState("start"); // "start" | "gameover"
  const [tipsOpen, setTipsOpen] = useState(false);
  const [soundLabel, setSoundLabel] = useState("🔇 Sound");

  // Imperative API exposed to React buttons
  const gameApiRef = useRef(null);

  // Prevent double-init (React StrictMode dev)
  const initializedRef = useRef(false);


const handleExit = () => {
  console.log("NEONDASH EXIT CLICKED");
  onExit?.();
};

  const overlayTitle = useMemo(() => {
    return overlayMode === "start" ? "Play" : "Game Over";
  }, [overlayMode]);

  const overlaySubtitle = useMemo(() => {
    return overlayMode === "start"
      ? "Tap / Space to jump. Double-tap / Shift to dash."
      : "Final run complete — beat your best.";
  }, [overlayMode]);

  const overlayKicker = "MIC FLAP";

  useEffect(() => {
    // StrictMode-safe: init only once per mount, reset on cleanup
    if (initializedRef.current) return;
    initializedRef.current = true;

    // ---------- Utilities ----------
    const rnd = (a = 1, b = 0) => Math.random() * (b - a) + a;
    const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
    const lerp = (a, b, t) => a + (b - a) * t;
    const now = () => performance.now();

    // ---------- Canvas / DPR ----------
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
    const DPR = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    const resize = () => {
      canvas.width = Math.floor(window.innerWidth * DPR);
      canvas.height = Math.floor(window.innerHeight * DPR);
    };
    window.addEventListener("resize", resize, { passive: true });
    resize();

    // ---------- Audio (WebAudio) ----------
    let audioCtx = null;
    let master = null;

    // Sound starts OFF, but we auto-enable it on first Start click (unless user manually muted).
    const soundOnRef = { current: false };
    const userMutedRef = { current: false }; // user explicitly turned sound off

    const ensureAudio = async () => {
      try {
        if (!audioCtx) {
          audioCtx = new (window.AudioContext || window.webkitAudioContext)();
          master = audioCtx.createGain();
          master.gain.value = 0.18; // louder so you can hear it
          master.connect(audioCtx.destination);
        }
        if (audioCtx.state === "suspended") {
          await audioCtx.resume();
        }
      } catch {
        // ignore
      }
    };

    const playTone = async ({ f = 440, d = 0.08, type = "triangle", gain = 1.0 } = {}) => {
      if (!soundOnRef.current) return;
      await ensureAudio();
      if (!audioCtx || !master) return;

      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = type;
      o.frequency.value = f;
      o.connect(g);
      g.connect(master);

      const t = audioCtx.currentTime;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(1.0 * gain, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d);

      o.start(t);
      o.stop(t + d + 0.02);
    };

    const playSeq = async (notes = []) => {
      if (!soundOnRef.current) return;
      await ensureAudio();
      if (!audioCtx || !master) return;

      let offset = 0;
      for (const n of notes) {
        const dt = typeof n.dt === "number" ? n.dt : 0;
        offset += dt;
        const t0 = audioCtx.currentTime + offset;

        const o = audioCtx.createOscillator();
        const g = audioCtx.createGain();
        o.type = n.type || "triangle";
        o.frequency.value = n.f || 440;
        o.connect(g);
        g.connect(master);

        const d = n.d ?? 0.08;
        const gain = n.gain ?? 1;

        g.gain.setValueAtTime(0.0001, t0);
        g.gain.linearRampToValueAtTime(1.0 * gain, t0 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + d);

        o.start(t0);
        o.stop(t0 + d + 0.02);

        offset += Math.max(0, d * 0.7);
      }
    };

    // --- SFX mapped to game events ---
    const sfx = {
      // UI / system
      soundOn: async () => {
        soundOnRef.current = true;
        userMutedRef.current = false;
        await ensureAudio();
        setSoundLabel("🔊 Sound");
        playSeq([
          { f: 660, d: 0.06, type: "triangle", gain: 0.9, dt: 0.0 },
          { f: 880, d: 0.07, type: "triangle", gain: 1.0, dt: 0.03 },
        ]);
      },
      soundOff: () => {
        userMutedRef.current = true;
        soundOnRef.current = false;
        setSoundLabel("🔇 Sound");
      },

      start: () =>
        playSeq([
          { f: 440, d: 0.06, type: "triangle", gain: 0.9, dt: 0.0 },
          { f: 660, d: 0.06, type: "triangle", gain: 0.9, dt: 0.03 },
          { f: 880, d: 0.08, type: "triangle", gain: 1.0, dt: 0.03 },
        ]),

      gameOver: (newBest) => {
        if (newBest) {
          playSeq([
            { f: 523, d: 0.08, type: "triangle", gain: 1.0, dt: 0.0 },
            { f: 659, d: 0.08, type: "triangle", gain: 1.0, dt: 0.03 },
            { f: 784, d: 0.10, type: "triangle", gain: 1.0, dt: 0.03 },
            { f: 1046, d: 0.12, type: "triangle", gain: 1.0, dt: 0.04 },
          ]);
        } else {
          playSeq([
            { f: 196, d: 0.10, type: "square", gain: 0.9, dt: 0.0 },
            { f: 165, d: 0.12, type: "square", gain: 0.8, dt: 0.03 },
            { f: 131, d: 0.14, type: "square", gain: 0.7, dt: 0.03 },
          ]);
        }
      },

      // Movement
      jump: () => playTone({ f: 520, d: 0.06, type: "triangle", gain: 0.9 }),
      dash: () => playTone({ f: 300, d: 0.08, type: "sawtooth", gain: 0.9 }),

      // Hits
      hazardHit: () =>
        playSeq([
          { f: 220, d: 0.07, type: "square", gain: 1.0, dt: 0.0 },
          { f: 160, d: 0.10, type: "square", gain: 0.9, dt: 0.02 },
        ]),
      shieldHit: () => playTone({ f: 260, d: 0.06, type: "square", gain: 0.9 }),

      // Pickups
      orb: (mult) => playTone({ f: 650 + mult * 35, d: 0.06, type: "triangle", gain: 0.85 }),
      shard: () => playTone({ f: 820, d: 0.07, type: "triangle", gain: 0.9 }),
      diamond: () =>
        playSeq([
          { f: 880, d: 0.07, type: "triangle", gain: 0.9, dt: 0.0 },
          { f: 1046, d: 0.10, type: "triangle", gain: 1.0, dt: 0.03 },
        ]),
      shieldPickup: () => playTone({ f: 500, d: 0.10, type: "sawtooth", gain: 0.9 }),

      recover: () => playTone({ f: 560, d: 0.07, type: "triangle", gain: 0.8 }),
    };

    // ---------- Tuning knobs ----------
    const STRIKE_DECAY_MS = 18000;
    const IFRAME_MS = 900;
    const MIN_HAZARD_GAP = 270;
    const JUMP_VY = -13.5;
    const GRAVITY = 0.48;
    const SCROLL_FACTOR = 1.9;

    // ---------- Game State ----------
    const state = {
      running: false,
      started: false,
      gameOver: false,
      t: 0,

      speed: 4,
      baseSpeed: 4,
      gravity: GRAVITY,

      score: 0,
      best: Number(localStorage.getItem("neondash_best") || 0),

      mult: 1,
      multTimer: 0,

      shield: 0,
      dashCD: 0,
      dashTime: 0,

      strikes: 0,
      strikeDecay: 0,
      iFrames: 0,

      worldX: 0,
      lastSpawn: 0,
      lastHazardX: -1e9,
      lastSegHadHazard: false,
    };

    // ---------- Entities ----------
    const player = { x: 160, y: 0, r: 18, vy: 0, onGround: false, color: "#41d9ff" };
    const platforms = [];
    const hazards = [];
    const orbs = [];
    const powerups = [];
    const particles = [];
    const gems = [];

    const spawnPlatform = (x, w, h) => platforms.push({ x, w, h });
    const spawnHazard = (x, y, w, h, type = "spike") => hazards.push({ x, y, w, h, type });
    const spawnOrb = (x, y) => orbs.push({ x, y, r: 8 });
    const spawnPower = (x, y) => powerups.push({ x, y, r: 10 });
    const spawnGem = (x, y, type) => gems.push({ x, y, type, r: type === "diamond" ? 10 : 8 });

    const seedWorld = () => {
      platforms.length = 0;
      hazards.length = 0;
      orbs.length = 0;
      powerups.length = 0;
      particles.length = 0;
      gems.length = 0;
      state.lastHazardX = -1e9;
      state.lastSegHadHazard = false;

      let x = 0;
      for (let i = 0; i < 14; i++) {
        const w = 220 + rnd(180);
        const h = 250 + Math.sin(i * 0.7) * 45 + rnd(-10, 10);
        spawnPlatform(x, w, h);

        if (i > 1 && Math.random() < 0.85) {
          const count = 3 + (Math.random() * 6) | 0;
          const baseY = h - 80 - rnd(40);
          for (let k = 0; k < count; k++) spawnOrb(x + 60 + k * 26, baseY - Math.sin(k * 0.6) * 12);
        }

        if (i > 2 && Math.random() < 0.65) {
          const gy = h - 115 - rnd(55);
          const gx = x + 90 + rnd(w - 180);
          const roll = Math.random();
          if (roll < 0.12) spawnGem(gx, gy, "diamond");
          else if (roll < 0.45) spawnGem(gx, gy, "shard");
        }

        if (i > 2) {
          const hazardBaseChance = 0.42;
          const penalty = state.lastSegHadHazard ? 0.18 : 0.0;
          const chance = Math.max(0.18, hazardBaseChance - penalty);

          if (Math.random() < chance) {
            const hx = x + 80 + rnd(w - 160);
            if (hx - state.lastHazardX >= MIN_HAZARD_GAP) {
              const type = Math.random() < 0.55 ? "spike" : "block";
              const hw = type === "spike" ? 26 : 30;
              const hh = type === "spike" ? 26 : 30;
              spawnHazard(hx, h - hh, hw, hh, type);
              state.lastHazardX = hx;
              state.lastSegHadHazard = true;
            } else {
              state.lastSegHadHazard = false;
            }
          } else {
            state.lastSegHadHazard = false;
          }
        }

        if (i > 3 && Math.random() < 0.16) {
          spawnPower(x + 80 + rnd(w - 160), h - 115 - rnd(55));
        }

        x += w + (95 + rnd(170));
      }
      state.lastSpawn = x;
    };

    seedWorld();
    player.y = platforms[0].h - player.r - 2;

    // ---------- Overlay helpers ----------
    const setHearts = (strikes) => {
      const host = ovHeartsRef.current;
      if (!host) return;
      const dots = Array.from(host.children);
      const left = Math.max(0, 3 - strikes);
      dots.forEach((d, i) => d.classList.toggle(styles.on, i < left));
    };

    const syncOverlayStats = () => {
      if (ovScoreRef.current) ovScoreRef.current.textContent = state.score.toLocaleString();
      if (ovBestRef.current) ovBestRef.current.textContent = state.best.toLocaleString();
      if (ovMultRef.current) ovMultRef.current.textContent = String(state.mult);
      setHearts(state.strikes);
    };

    const showStartOverlay = () => {
      setTipsOpen(false);
      setOverlayMode("start");
      syncOverlayStats();
      setOverlayVisible(true);
      if (ovFooterLeftRef.current) {
        ovFooterLeftRef.current.textContent = "Designed for portfolios — quick, smooth, addictive.";
      }
    };

    const showGameOverOverlay = () => {
      setTipsOpen(false);
      setOverlayMode("gameover");
      syncOverlayStats();
      setOverlayVisible(true);
      if (ovFooterLeftRef.current) {
        ovFooterLeftRef.current.textContent = "Tip: short taps = small hops. Hold = higher jumps.";
      }
    };

    const hideOverlay = () => {
      setOverlayVisible(false);
    };

    // show overlay initially
    showStartOverlay();

    // ---------- Input ----------
    const keys = new Set();
    let tapped = false;
    let lastTap = 0;

    const wantJump = () => keys.has("Space") || keys.has("KeyW") || keys.has("ArrowUp") || tapped;
    const wantDash = () => keys.has("ShiftLeft") || keys.has("ShiftRight");

    const onKeyDown = (e) => {
      keys.add(e.code);
      if (e.code === "KeyP") togglePause();
      if (e.code === "KeyR") restartRun();
    };
    const onKeyUp = (e) => keys.delete(e.code);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    const onTap = () => {
      const t = now();
      tapped = true;

      if (!state.started) {
        startGame();
        window.setTimeout(() => (tapped = false), 60);
        return;
      }

      if (t - lastTap < 260) doDash();
      lastTap = t;
      window.setTimeout(() => (tapped = false), 60);
    };

    canvas.addEventListener("pointerdown", onTap, { passive: true });

    // Keyboard focus and prevent scroll on jump keys
    const onCanvasKeyDown = (e) => {
      if (["Space", "KeyW", "ArrowUp"].includes(e.code)) e.preventDefault();
    };
    const onCanvasClick = () => canvas.focus();

    canvas.addEventListener("keydown", onCanvasKeyDown, { capture: true });
    canvas.addEventListener("click", onCanvasClick);

    // ---------- UI helpers ----------
    const flashStatus = (msg) => {
      if (statusCardRef.current) statusCardRef.current.textContent = msg;
      window.setTimeout(() => {
        if (!state.gameOver && state.running && statusCardRef.current) statusCardRef.current.textContent = "Go!";
      }, 900);
    };

    const toast = (msg) => {
      const el = toastRef.current;
      if (!el) return;
      el.textContent = msg;
      el.style.display = "block";
      el.style.opacity = "1";
      window.setTimeout(() => (el.style.opacity = "0"), 700);
      window.setTimeout(() => (el.style.display = "none"), 1000);
    };

    const makeBurst = (x, y, n, color) => {
      const arr = [];
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const s = 1.2 + Math.random() * 3.2;
        arr.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 200 + Math.random() * 300, color });
      }
      return arr;
    };

    // ---------- Controls ----------
    const jump = () => {
      if (player.onGround) {
        player.vy = JUMP_VY;
        player.onGround = false;
        particles.push(...makeBurst(player.x, player.y + player.r, 14, "#41d9ff"));
        sfx.jump();
      }
    };

    const doDash = () => {
      if (state.dashCD > 0 || state.dashTime > 0) return;
      state.dashTime = 200;
      state.dashCD = 900;
      state.speed += 2;
      state.shield = Math.max(state.shield, 260);
      particles.push(...makeBurst(player.x + player.r, player.y, 22, "#7bff8c"));
      sfx.dash();
      flashStatus("DASH!");
    };

    const togglePause = () => {
      if (!state.started) return;
      state.running = !state.running;
      if (statusCardRef.current) statusCardRef.current.textContent = state.running ? "Go!" : "Paused";
    };

    // ---------- Spawning ----------
    const extendWorld = () => {
      const need = canvas.width / DPR + 900;
      while (state.lastSpawn - state.worldX < need) {
        const baseH = 250 + Math.sin(state.lastSpawn / 320) * 60 + rnd(-18, 18);
        const w = 220 + rnd(240);
        const segX = state.lastSpawn;

        spawnPlatform(segX, w, baseH);

        if (Math.random() < 0.88) {
          const n = 4 + ((Math.random() * 6) | 0);
          const baseY = baseH - 70 - rnd(30);
          for (let i = 0; i < n; i++) spawnOrb(segX + 60 + i * 26, baseY - Math.sin(i * 0.7) * 12);
        }

        if (Math.random() < 0.6) {
          const gy = baseH - 115 - rnd(55);
          const gx = segX + 95 + rnd(w - 190);
          const roll = Math.random();
          if (roll < 0.11) spawnGem(gx, gy, "diamond");
          else if (roll < 0.42) spawnGem(gx, gy, "shard");
        }

        const hazardBaseChance = 0.4;
        const penalty = state.lastSegHadHazard ? 0.2 : 0.0;
        const chance = Math.max(0.16, hazardBaseChance - penalty);

        let placedHazard = false;
        if (Math.random() < chance) {
          const hx = segX + 85 + rnd(w - 170);
          if (hx - state.lastHazardX >= MIN_HAZARD_GAP) {
            const type = Math.random() < 0.55 ? "spike" : "block";
            const hw = type === "spike" ? 26 : 30;
            const hh = type === "spike" ? 26 : 30;
            spawnHazard(hx, baseH - hh, hw, hh, type);
            state.lastHazardX = hx;
            placedHazard = true;
          }
        }
        state.lastSegHadHazard = placedHazard;

        if (Math.random() < 0.16) {
          spawnPower(segX + 90 + rnd(w - 180), baseH - 115 - rnd(55));
        }

        state.lastSpawn += w + (95 + rnd(175));
      }
    };

    // ---------- Collisions ----------
    const rectIntersect = (ax, ay, aw, ah, bx, by, bw, bh) =>
      ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;

    const onHit = () => {
      if (state.iFrames > 0) return;

      if (state.shield > 0) {
        state.shield = Math.max(0, state.shield - 1200);
        toast("Shield hit 🛡");
        particles.push(...makeBurst(player.x, player.y, 18, "#ffd166"));
        sfx.shieldHit();
        state.iFrames = IFRAME_MS * 0.6;
        return;
      }

      state.strikes = Math.min(3, state.strikes + 1);
      state.strikeDecay = STRIKE_DECAY_MS;
      state.iFrames = IFRAME_MS;

      state.mult = Math.max(1, state.mult - 2);
      state.multTimer = Math.min(state.multTimer, 800);

      if (strikesRef.current) strikesRef.current.textContent = String(state.strikes);
      flashStatus(`Strike ${state.strikes}/3`);
      toast(`⚠ Strike ${state.strikes}/3`);
      particles.push(...makeBurst(player.x, player.y, 26, "#ff5b7e"));
      sfx.hazardHit();

      if (state.strikes >= 3) gameOver();
    };

    const addScore = (s) => {
      state.score += s;
      let newBest = false;
      if (state.score > state.best) {
        state.best = state.score;
        localStorage.setItem("neondash_best", String(state.best));
        newBest = true;
      }
      return newBest;
    };

    const update = (dt) => {
      state.baseSpeed = 4 + Math.min(7, state.t / 360000);
      const targetSpeed = state.baseSpeed + (state.dashTime > 0 ? 2 : 0);
      state.speed = lerp(state.speed, targetSpeed, 0.06);

      if (state.multTimer > 0) state.multTimer = Math.max(0, state.multTimer - dt);
      if (state.dashTime > 0) state.dashTime -= dt;
      if (state.dashCD > 0) state.dashCD -= dt;
      if (state.shield > 0) state.shield -= dt;
      if (state.iFrames > 0) state.iFrames -= dt;

      if (state.strikes > 0) {
        state.strikeDecay -= dt;
        if (state.strikeDecay <= 0) {
          state.strikes -= 1;
          if (strikesRef.current) strikesRef.current.textContent = String(state.strikes);
          state.strikeDecay = state.strikes > 0 ? STRIKE_DECAY_MS : 0;
          flashStatus("Recovered ✨");
          toast("Recovered ✨");
          sfx.recover();
        }
      }

      if (shieldRef.current) shieldRef.current.textContent = (state.shield / 1000).toFixed(1) + "s";

      if (wantJump()) {
        if (player.onGround) jump();
      }
      if (!wantJump() && player.vy < -4) player.vy = -4;
      if (wantDash()) doDash();

      player.vy += state.gravity;
      player.y += player.vy;

      const dx = state.speed * (dt / 16.6667) * SCROLL_FACTOR;
      state.worldX += dx;

      // Ground collision
      player.onGround = false;
      let groundY = Infinity;
      for (const p of platforms) {
        const px = p.x - state.worldX;
        if (px + p.w < player.x - player.r - 90) continue;
        if (px > player.x + player.r + 50) break;
        if (player.x > px && player.x < px + p.w) {
          groundY = Math.min(groundY, p.h - player.r - 1);
        }
      }
      if (player.y > groundY) {
        player.y = groundY;
        player.vy = Math.min(player.vy, 0);
        player.onGround = true;
      }

      // Hazards
      for (let i = hazards.length - 1; i >= 0; i--) {
        const h = hazards[i];
        const hx = h.x - state.worldX;
        if (hx + h.w < -90) {
          hazards.splice(i, 1);
          continue;
        }
        if (Math.abs(hx - player.x) < 90) {
          if (
            rectIntersect(
              player.x - player.r,
              player.y - player.r,
              player.r * 2,
              player.r * 2,
              hx,
              h.y - h.h,
              h.w,
              h.h
            )
          ) {
            onHit();
          }
        }
      }

      // Orbs (light blue)
      for (let i = orbs.length - 1; i >= 0; i--) {
        const o = orbs[i];
        const ox = o.x - state.worldX;
        if (ox + o.r < -90) {
          orbs.splice(i, 1);
          continue;
        }
        const dxp = ox - player.x;
        const dyp = o.y - player.y;
        const d2 = dxp * dxp + dyp * dyp;
        if (d2 < (player.r + o.r) * (player.r + o.r)) {
          orbs.splice(i, 1);
          addScore(70 * state.mult);
          state.mult = Math.min(10, state.mult + 1);
          state.multTimer = 2200;
          toast(`+${70 * state.mult}  x${state.mult}`);
          particles.push(...makeBurst(player.x, player.y, 16, "#41d9ff"));

          // orb + multiplier sound (pitch rises with mult)
          sfx.orb(state.mult);
        }
      }

      // Gems
      for (let i = gems.length - 1; i >= 0; i--) {
        const g = gems[i];
        const gx = g.x - state.worldX;
        if (gx + g.r < -90) {
          gems.splice(i, 1);
          continue;
        }
        const dxp = gx - player.x;
        const dyp = g.y - player.y;
        const d2 = dxp * dxp + dyp * dyp;
        if (d2 < (player.r + g.r) * (player.r + g.r)) {
          gems.splice(i, 1);
          if (g.type === "diamond") {
            addScore(600);
            toast("💎 +600");
            particles.push(...makeBurst(player.x, player.y, 28, "#7bff8c"));
            sfx.diamond();
          } else {
            addScore(180);
            toast("✦ +180");
            particles.push(...makeBurst(player.x, player.y, 18, "#41d9ff"));
            sfx.shard();
          }
        }
      }

      // Powerups (shield pickup)
      for (let i = powerups.length - 1; i >= 0; i--) {
        const p = powerups[i];
        const px = p.x - state.worldX;
        if (px + p.r < -90) {
          powerups.splice(i, 1);
          continue;
        }
        const dxp = px - player.x;
        const dyp = p.y - player.y;
        const d2 = dxp * dxp + dyp * dyp;
        if (d2 < (player.r + p.r) * (player.r + p.r)) {
          powerups.splice(i, 1);
          state.shield = 6500;
          toast("Shield +6.5s 🛡");
          particles.push(...makeBurst(player.x, player.y, 24, "#ffd166"));
          sfx.shieldPickup();
        }
      }

      // Distance score
      addScore(Math.floor(dx * 1.4));

      // Particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);
        p.life -= dt;
        if (p.life <= 0) particles.splice(i, 1);
      }

      extendWorld();

      // UI updates (imperative)
      if (scoreRef.current) scoreRef.current.textContent = state.score.toLocaleString();
      if (bestRef.current) bestRef.current.textContent = state.best.toLocaleString();
      if (multRef.current) multRef.current.textContent = String(state.mult);

      syncOverlayStats();
    };

    const gameOver = () => {
      if (state.gameOver) return;
      state.gameOver = true;
      state.running = false;
      if (statusCardRef.current) statusCardRef.current.textContent = "Game Over";
      particles.push(...makeBurst(player.x, player.y, 32, "#ff5b7e"));

      // ✅ Game over results sound on popup
      const newBest = state.score >= state.best;
      sfx.gameOver(newBest);

      showGameOverOverlay();
    };

    const restartRun = () => {
      Object.assign(state, {
        running: true,
        gameOver: false,
        t: 0,
        speed: 4,
        baseSpeed: 4,
        gravity: GRAVITY,
        score: 0,
        mult: 1,
        multTimer: 0,
        shield: 0,
        dashCD: 0,
        dashTime: 0,
        strikes: 0,
        strikeDecay: 0,
        iFrames: 0,
        worldX: 0,
        lastSpawn: 0,
        lastHazardX: -1e9,
        lastSegHadHazard: false,
      });

      if (strikesRef.current) strikesRef.current.textContent = String(state.strikes);
      if (bestRef.current) bestRef.current.textContent = state.best.toLocaleString();

      seedWorld();
      player.x = 160;
      player.vy = 0;
      player.onGround = false;
      player.y = platforms[0].h - player.r - 2;

      if (statusCardRef.current) statusCardRef.current.textContent = "Go!";
      hideOverlay();
    };

    const startGame = async () => {
      // Audio unlock happens here (user gesture from Start click)
      // Auto-enable sound on first start unless user explicitly muted
      if (!soundOnRef.current && !userMutedRef.current) {
        await sfx.soundOn();
      } else {
        await ensureAudio(); // still unlock in case it's suspended
      }

      hideOverlay();
      restartRun();
      state.started = true;
      if (statusCardRef.current) statusCardRef.current.textContent = "Go!";
      canvas.focus();

      // Start jingle
      sfx.start();
    };

    // ---------- Rendering ----------
    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.fillStyle = "#070a12";
      ctx.fillRect(0, 0, w, h);

      // Stars
      ctx.save();
      for (let layer = 1; layer <= 3; layer++) {
        const par = 0.1 * layer;
        const step = 40 * layer;
        ctx.globalAlpha = 0.2 + layer * 0.15;
        for (let x = -((state.worldX * par * DPR) % step); x < w; x += step) {
          for (let y = (layer * 13) % step; y < h; y += step) {
            ctx.fillStyle = layer === 3 ? "#2bd4ff" : "#0f2a44";
            ctx.fillRect(x, y, 2, 2);
          }
        }
      }
      ctx.restore();

      // Platforms
      ctx.lineWidth = 2 * DPR;
      for (const p of platforms) {
        const x = (p.x - state.worldX) * DPR;
        const y = p.h * DPR;
        const W = p.w * DPR;
        ctx.strokeStyle = "#183e66";
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + W, y);
        ctx.stroke();
        ctx.strokeStyle = "rgba(65,217,255,.12)";
        ctx.beginPath();
        ctx.moveTo(x, y - 2 * DPR);
        ctx.lineTo(x + W, y - 2 * DPR);
        ctx.stroke();
      }

      // Hazards
      for (const hz of hazards) {
        const x = (hz.x - state.worldX) * DPR;
        const y = hz.y * DPR;
        ctx.save();
        if (hz.type === "spike") {
          const s = hz.w * DPR;
          ctx.fillStyle = "#ff5b7e";
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + s * 0.5, y - s * 0.9);
          ctx.lineTo(x + s, y);
          ctx.closePath();
          ctx.shadowColor = "#ff5b7e";
          ctx.shadowBlur = 12;
          ctx.fill();
        } else {
          ctx.fillStyle = "#ff5b7e";
          ctx.shadowColor = "#ff5b7e";
          ctx.shadowBlur = 10;
          ctx.fillRect(x, y - hz.h * DPR, hz.w * DPR, hz.h * DPR);
        }
        ctx.restore();
      }

      // Orbs
      for (const o of orbs) {
        const x = (o.x - state.worldX) * DPR;
        const y = o.y * DPR;
        const r = o.r * DPR;
        ctx.save();
        ctx.fillStyle = "#41d9ff";
        ctx.shadowColor = "#41d9ff";
        ctx.shadowBlur = 15;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Gems
      for (const g of gems) {
        const x = (g.x - state.worldX) * DPR;
        const y = g.y * DPR;
        const r = g.r * DPR;
        ctx.save();
        if (g.type === "diamond") {
          ctx.fillStyle = "#7bff8c";
          ctx.shadowColor = "#7bff8c";
          ctx.shadowBlur = 22;
          ctx.beginPath();
          ctx.moveTo(x, y - r);
          ctx.lineTo(x + r, y);
          ctx.lineTo(x, y + r);
          ctx.lineTo(x - r, y);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.fillStyle = "#41d9ff";
          ctx.shadowColor = "#41d9ff";
          ctx.shadowBlur = 14;
          ctx.beginPath();
          ctx.moveTo(x, y - r);
          ctx.lineTo(x + r * 0.8, y + r * 0.2);
          ctx.lineTo(x, y + r);
          ctx.lineTo(x - r * 0.8, y + r * 0.2);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }

      // Powerups
      for (const p of powerups) {
        const x = (p.x - state.worldX) * DPR;
        const y = p.y * DPR;
        const r = p.r * DPR;
        ctx.save();
        ctx.fillStyle = "#ffd166";
        ctx.shadowColor = "#ffd166";
        ctx.shadowBlur = 22;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#0a0f1a";
        ctx.beginPath();
        ctx.arc(x, y, r * 0.55, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Player (blink during iFrames)
      ctx.save();
      const px = player.x * DPR;
      const py = player.y * DPR;
      const pr = player.r * DPR;
      const blink = state.iFrames > 0 ? (Math.sin(state.t * 0.04) > 0 ? 0.25 : 1) : 1;
      ctx.globalAlpha = blink;
      ctx.fillStyle = state.shield > 0 ? "#7bff8c" : player.color;
      ctx.shadowColor = ctx.fillStyle;
      ctx.shadowBlur = state.dashTime > 0 ? 28 : 14;
      ctx.beginPath();
      ctx.arc(px, py, pr, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 0.22 * blink;
      ctx.beginPath();
      ctx.ellipse(px - 18 * DPR, py + 8 * DPR, pr * 1.2, pr * 0.6, -0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Particles
      for (const p of particles) {
        ctx.save();
        ctx.globalAlpha = clamp(p.life / 400, 0, 1);
        ctx.fillStyle = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 12;
        ctx.fillRect(p.x * DPR, p.y * DPR, 3 * DPR, 3 * DPR);
        ctx.restore();
      }

      // Pause indicator
      if (state.started && !state.running && !state.gameOver) {
        ctx.save();
        ctx.translate(w / 2, h / 2);
        ctx.textAlign = "center";
        ctx.fillStyle = "#d9efff";
        ctx.font = `${32 * DPR}px system-ui, Segoe UI, Roboto`;
        ctx.shadowColor = "#41d9ff";
        ctx.shadowBlur = 18;
        ctx.fillText("Paused", 0, -18 * DPR);
        ctx.font = `${18 * DPR}px system-ui, Segoe UI, Roboto`;
        ctx.shadowBlur = 0;
        ctx.fillStyle = "rgba(233,246,255,.85)";
        ctx.fillText("Press P to resume", 0, 14 * DPR);
        ctx.restore();
      }
    };

    // ---------- Loop ----------
    let rafId = 0;
    let last = now();

    const frame = () => {
      const t = now();
      let dt = t - last;
      last = t;
      dt = Math.min(40, dt);

      if (state.running && !state.gameOver) {
        state.t += dt;
        update(dt);
      } else if (state.started) {
        state.t += dt * 0.5;
      }

      draw();
      rafId = requestAnimationFrame(frame);
    };
    rafId = requestAnimationFrame(frame);

    // ---------- Expose API to React controls ----------
    gameApiRef.current = {
      togglePause,
      restartRun,
      startGame,
      showStartOverlay,
      showGameOverOverlay,
      setSoundOn: async (on) => {
        if (on) await sfx.soundOn();
        else sfx.soundOff();
      },
      getSoundOn: () => soundOnRef.current,
    };

    // Init HUD values
    if (bestRef.current) bestRef.current.textContent = state.best.toLocaleString();
    if (strikesRef.current) strikesRef.current.textContent = String(state.strikes);
    if (statusCardRef.current) statusCardRef.current.textContent = "Press Start to begin 💫";

    // Cleanup
    return () => {
      initializedRef.current = false; // ✅ allow proper re-init in StrictMode

      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      canvas.removeEventListener("pointerdown", onTap);
      canvas.removeEventListener("keydown", onCanvasKeyDown, { capture: true });
      canvas.removeEventListener("click", onCanvasClick);

      try {
        if (audioCtx && audioCtx.state !== "closed") audioCtx.close();
      } catch {
        // ignore
      }

      gameApiRef.current = null;
    };
  }, []);

  // --- React handlers calling imperative API ---
  const onStart = useCallback(() => {
    gameApiRef.current?.startGame();
  }, []);

  const onPlayAgain = useCallback(() => {
    gameApiRef.current?.startGame();
  }, []);

  const onPause = useCallback(() => {
    gameApiRef.current?.togglePause();
  }, []);

  const onRestart = useCallback(() => {
    gameApiRef.current?.restartRun();
  }, []);

  const onToggleSound = useCallback(async () => {
    const api = gameApiRef.current;
    if (!api) return;
    const next = !api.getSoundOn();
    await api.setSoundOn(next);
  }, []);

  const onCopyScore = useCallback(async () => {
    const score = ovScoreRef.current?.textContent ?? "0";
    const best = ovBestRef.current?.textContent ?? "0";
    const text = `MIC FLAP — Score: ${score} | Best: ${best}`;
    try {
      await navigator.clipboard.writeText(text);
      const el = toastRef.current;
      if (el) {
        el.textContent = "Copied!";
        el.style.display = "block";
        el.style.opacity = "1";
        window.setTimeout(() => (el.style.opacity = "0"), 700);
        window.setTimeout(() => (el.style.display = "none"), 1000);
      }
    } catch {
      const el = toastRef.current;
      if (el) {
        el.textContent = "Copy blocked";
        el.style.display = "block";
        el.style.opacity = "1";
        window.setTimeout(() => (el.style.opacity = "0"), 700);
        window.setTimeout(() => (el.style.display = "none"), 1000);
      }
    }
  }, []);

  const onToggleTips = useCallback(() => {
    setTipsOpen((v) => !v);
  }, []);

  return (
    <div className={styles.root}>
      <canvas ref={canvasRef} className={styles.canvas} aria-label="Mic Flap Game" tabIndex={0} />

      {/* Overlay */}
      <div
        className={`${styles.overlay} ${overlayVisible ? styles.show : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="overlayTitle"
      >
        <div className={styles.overlayCard}>
          <div className={styles.overlayHeader}>
            <div>
              <div className={styles.overlayKicker}>{overlayKicker}</div>
              <h2 className={styles.overlayTitle} id="overlayTitle">
                {overlayTitle}
              </h2>
              <div className={styles.overlaySubtitle}>{overlaySubtitle}</div>
            </div>

            <div className={styles.overlayHeaderRight}>
              <div className={styles.overlayBadge}>💎</div>

              {/* Exit icon */}
              <button
                className={styles.exitBtn}
                type="button"
                aria-label="Exit to main page"
                title="Exit"
                onClick={handleExit}
              >
                ✕
              </button>
            </div>
          </div>

          <div className={styles.statGrid}>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Score</div>
              <div className={styles.statValueBig} ref={ovScoreRef}>
                0
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Best</div>
              <div className={styles.statValue} ref={ovBestRef}>
                0
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Multiplier</div>
              <div className={styles.statValue}>
                x<span ref={ovMultRef}>1</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Strikes</div>
              <div className={styles.hearts} ref={ovHeartsRef} aria-label="Strikes remaining">
                <span className={`${styles.heart} ${styles.on}`} />
                <span className={`${styles.heart} ${styles.on}`} />
                <span className={`${styles.heart} ${styles.on}`} />
              </div>
            </div>
          </div>

          <div className={styles.overlayGrid}>
            <div>
              <div className={styles.pillRow}>
                <div className={styles.howPill}>
                  <span>⤴︎</span>
                  <span className={styles.key}>Space</span>
                  <span className={styles.key}>W</span>
                  <span className={styles.key}>↑</span>
                  <span className={styles.howText}>Jump</span>
                </div>

                <div className={styles.howPill}>
                  <span>⚡</span>
                  <span className={styles.key}>Shift</span>
                  <span className={styles.howText}>Dash</span>
                  <span className={styles.howMuted}>(or double-tap)</span>
                </div>

                <div className={styles.howPill}>
                  <span>⏸</span>
                  <span className={styles.key}>P</span>
                  <span className={styles.howText}>Pause</span>
                </div>

                <div className={styles.howPill}>
                  <span>↻</span>
                  <span className={styles.key}>R</span>
                  <span className={styles.howText}>Restart</span>
                </div>
              </div>

              {/* <div className={`${styles.tips} ${tipsOpen ? styles.showTips : ""}`}>
                <div className={styles.tip}>
                  <b>Avoid</b> pink hazards — don’t rush jumps.
                </div>
                <div className={styles.tip}>
                  <b>Short tap</b> = small hop. <b>Hold</b> = higher jump.
                </div>
                <div className={styles.tip}>
                  Use <b>dash</b> to save yourself (cooldown applies).
                </div>
              </div> */}

              <div className={`${styles.tips} ${tipsOpen ? styles.showTips : ""}`}>
                <div className={styles.tip}>
                  <b>Avoid Pink Hazards</b>
                  <span className={styles.tipMeta}>Touching them costs a strike</span>
                  <span className={styles.tipIcon} aria-hidden>🟥</span>
                </div>

                <div className={styles.tip}>
                  <b>Multipliers</b>
                  <span className={styles.tipMeta}>Collect 💎 to boost points faster</span>
                  <span className={styles.tipIcon} aria-hidden>💎</span>
               </div>

  <div className={styles.tip}>
    <b>Controls</b>
    <span className={styles.tipMeta}>Tap / Space to jump • Shift / double-tap to dash</span>
    <span className={styles.tipIcon} aria-hidden>🕹️</span>
  </div>
</div>
</div>

            <div className={styles.pickupGrid}>
              <div className={styles.pickup}>
                <span className={`${styles.dot} ${styles.cyan}`} />
                <div>
                  <div className={styles.pickupTitle}>Cyan orbs</div>
                  <div className={styles.pickupSub}>Build multiplier</div>
                </div>
              </div>

              <div className={styles.pickup}>
                <span className={styles.pickupIconGreen}>💎</span>
                <div>
                  <div className={styles.pickupTitle}>Diamond</div>
                  <div className={styles.pickupSub}>Big bonus</div>
                </div>
              </div>

              <div className={styles.pickup}>
                <span className={styles.pickupIconCyan}>✦</span>
                <div>
                  <div className={styles.pickupTitle}>Shard</div>
                  <div className={styles.pickupSub}>Small bonus</div>
                </div>
              </div>

              <div className={styles.pickup}>
                <span className={styles.pickupIconGold}>🛡</span>
                <div>
                  <div className={styles.pickupTitle}>Shield</div>
                  <div className={styles.pickupSub}>Temporary safety</div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.overlayFooter}>

            <div className={styles.overlayActions}>
              {overlayMode === "start" ? (
                <button className={`${styles.ovBtn} ${styles.primary}`} type="button" onClick={onStart}>
                  ▶ Start
                </button>
              ) : (
                <button className={`${styles.ovBtn} ${styles.primary}`} type="button" onClick={onPlayAgain}>
                  ↻ Play again
                </button>
              )}

              <button className={styles.ovBtn} type="button" onClick={onToggleTips}>
                ℹ Tips
              </button>
              <button className={styles.ovBtn} type="button" onClick={onCopyScore}>
                ⎘ Copy score
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* HUD */}
      <div className={styles.hud}>
        <div className={styles.topbar}>
          <div className={`${styles.card} ${styles.title}`}>NEON&nbsp;DASH</div>

          <div className={styles.stats}>
            <div className={styles.pill}>
              Score: <b ref={scoreRef}>0</b>
            </div>
            <div className={styles.pill}>
              Best: <b ref={bestRef}>0</b>
            </div>
            <div className={styles.pill}>
              x<b ref={multRef}>1</b>
            </div>
            <div className={styles.pill} title="Shield time">
              <span className={styles.shieldBadge}>🛡</span> <b ref={shieldRef}>0.0s</b>
            </div>
            <div className={`${styles.pill} ${styles.warn}`} title="You fail only after 3 strikes. Strikes decay over time.">
              Strikes: <b ref={strikesRef}>0</b>/3
            </div>
          </div>

          <div className={styles.actions}>
            <button className={styles.btn} type="button" onClick={onPause}>
              ⏸ Pause
            </button>
            <button className={styles.btn} type="button" onClick={onRestart}>
              🔁 Restart
            </button>
            <button className={`${styles.btn} ${styles.warnBtn}`} type="button" onClick={onToggleSound}>
              {soundLabel}
            </button>
          </div>
        </div>

        <div />

        <div className={styles.bottombar}>
          <div className={`${styles.card} ${styles.muted}`}>
            Jump: Space/Tap • Dash: Shift/double-tap • P pause • R restart
          </div>
          <div className={styles.card} ref={statusCardRef}>
            Press Start to begin 💫
          </div>
        </div>
      </div>

      <div ref={toastRef} className={styles.toast} style={{ display: "none" }}>
        +100
      </div>
    </div>
  );
}
